import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import skillApi from '../../api/skillApi'; // Import skillApi
import { useToast } from '../../contexts/ToastContext'; // Import useToast

const AddSkill = () => {
  const navigate = useNavigate();
  const { addToast } = useToast(); // Initialize useToast
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState(null); // For general API errors
  const [validationErrors, setValidationErrors] = useState({}); // For field-specific validation errors
  const [formData, setFormData] = useState({
    name: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear validation error for the field being changed
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({}); // Clear previous validation errors

    try {
      const response = await skillApi.createSkill(formData);
      if (response.success) {
        addToast('Skill created successfully!', 'success');
        navigate('/skills');
      } else {
        // Handle API errors that are not validation errors (e.g., server error)
        setGeneralError(response.message || 'Failed to create skill.');
        addToast(response.message || 'Failed to create skill.', 'error');
      }
    } catch (err) {
      console.error('Error creating skill:', err);
      if (err.response && err.response.status === 422) {
        // Handle validation errors
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        // Handle other API errors
        setGeneralError(err.response?.data?.message || 'Failed to create skill. Please try again.');
        addToast(err.response?.data?.message || 'Failed to create skill. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Add New Skill</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      {loading ? (
        <SkeletonLoader type="form" />
      ) : (
        <form onSubmit={handleSubmit}>
          <TextInput
            label="Skill Name"
            name="name"
            id="name"
            value={formData.name}
            onChange={handleChange}
            required={true} // Mark as required
            placeholder="Skill Name"
            error={validationErrors?.name?.[0]} // Display field-specific error
          />
          <div className="flex items-center justify-between mt-6">
            <Button
              type="submit"
              className="bg-accent-red hover:bg-red-700 text-white"
              loading={loading}
            >
              Add Skill
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/skills')}
              className="bg-gray-500 hover:bg-gray-600 text-white"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddSkill;